import React, { useEffect, useMemo, useRef, useState } from 'react'
import SVG from 'react-inlinesvg'
import { useLayout } from '../../../_metronic/layout/core'
import { MembershipDetailModel } from '../memberships/models/MembershipModel'
import { KTSVG } from '../../helpers/KTSVG'
import { getAllMemberships } from '../../services/membershipService'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '../../../setup'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import { useHistory } from 'react-router-dom'
import { showErrorAlert } from '../../helpers/alert'


const SECTION_REQ = '__SECTION_REQUIREMENTS__'
const REQUIREMENT_ROW = '__REQUIREMENT_ROW__'

const ChangeMembershipPage: React.FC<Props> = () => {
    const { config, setLayout } = useLayout()
    const restoreRef = useRef(config)
    const subscription = useSelector(
        (state: RootState) => state.auth?.subscription,
        shallowEqual
    )
    const [hoverIdx, setHoverIdx] = useState<number | null>(null)
    const [selectedIdx, setSelectedIdx] = useState<number | null>(null)
    const [memberships, setMemberships] = useState<any[]>([])
    const [membershipUpgrade, setMembershipUpgrade] = useState<any | null>(null)
    const [currentMembership, setCurrentMembership] = useState<any | null>(null)
    const [loading, setLoading] = useState<boolean>(false)
    const history = useHistory();

    useEffect(() => {
        restoreRef.current = config
        setLayout({
            ...config,
            header: { ...config.header, display: false },
            toolbar: { ...config.toolbar, display: false },
            aside: { ...config.aside, display: false },
            footer: { ...config.footer, display: false },
            main: {
                ...config.main,
                body: { ...config.main?.body, class: 'app-blank' },
            },
        })
        document.body.classList.add('bg-white')
        initMemberships()
        return () => {
            setLayout(restoreRef.current)
            document.body.classList.remove('bg-white')
        }
    }, [])

    const initMemberships = async () => {
        const mems = await getAllMemberships()
        const data = mems.data

        const currentId = subscription?.plan?.id
        const current = data.find((m: any) => m.id === currentId)
        setCurrentMembership(current)

        const currentPlanTitle = (subscription?.plan?.title || current?.title || '').toLowerCase().trim()
        const isHighTier = currentPlanTitle.includes('founding') || currentPlanTitle.includes('electi+') || currentPlanTitle.includes('plus')

        if (isHighTier) {
            // Si ya es Founding Member* o Electi+, no se permite el acceso a cambiar plan
            history.replace('/financial')
            return
        }

        // Si es Electi, solo debe aparecer "Electi+"
        const electiPlusPlan = data.find((m: any) => m.title?.toLowerCase().includes('electi+') || m.title?.toLowerCase().includes('plus'))
        if (electiPlusPlan) {
            setMemberships([electiPlusPlan])
            setMembershipUpgrade(electiPlusPlan)
            setSelectedIdx(electiPlusPlan.id)
        } else {
            const filtered = data.filter((m: any) => !m.title?.includes('Founding Member') && m.id !== currentId)
            setMemberships(filtered)
        }
    }

    const allBooleanFeatures = useMemo(
        () => [
            'memberIntroduction',
            'joinExperiences',
            'hostPrivateExperiences',
            'hostMastermindCircles',
            'presentAsExpert',
            'incognitoMode',
            'earlyAccessToPreLaunchExperiences',
        ],
        []
    )
    const headRef = useRef<HTMLDivElement | null>(null)
    const labelRefs = useRef<HTMLDivElement[]>([])
    const [headH, setHeadH] = useState<number>(174)
    const [rowHeights, setRowHeights] = useState<number[]>([])
    const zoomRef = useRef<HTMLDivElement | null>(null)
    const boardRef = useRef<HTMLDivElement | null>(null)

    const labelRows = useMemo(
        () => [...allBooleanFeatures],
        [allBooleanFeatures]
    )

    useEffect(() => {
        const measure = () => {
            if (headRef.current) setHeadH(headRef.current.offsetHeight || 174)
            const hs = labelRefs.current.map((el) => (el ? el.offsetHeight || 44 : 44))
            setRowHeights(hs)
        }
        requestAnimationFrame(measure)
        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [memberships.length])

    if (loading) {
        return (
            <div className='d-flex justify-content-center align-items-center' style={{ minHeight: 400 }}>
                <span className='spinner-border text-dark' role='status' />
            </div>
        )
    }

    const getBooleanFieldValue = (m: any, label: string): boolean | null => {
        if (m.hasOwnProperty(label)) {
            return m[label]
        }
        return null
    }

    const getLabelDisplayName = (label: string): string => {
        const labelMap: Record<string, string> = {
            memberIntroduction: 'Member Introduction',
            joinExperiences: 'Join Experiences',
            hostPrivateExperiences: 'Host Private Experiences',
            hostMastermindCircles: 'Host Mastermind Circles',
            presentAsExpert: 'Present as Expert',
            incognitoMode: 'Incognito Mode',
            earlyAccessToPreLaunchExperiences: 'Early Access to Experiences',
        }
        return labelMap[label] || label
    }

    const getSpecialText = (m: any, label: string): string | null => {
        if (label === 'memberIntroduction') {
            return m.memberIntroductionLabel
        }
        return null
    }

    const getRequirementText = (m: any): string => {
        if (m.requirements && m.requirements.length > 0) {
            const fullReq = m.requirements[0]
            const parts = fullReq.split('|')
            if (parts.length === 2) {
                const status = parts[1].trim()
                if (status === 'Required') return 'Required**'
                if (status === 'Not Required') return 'Not Required'
            }
        }
        return ''
    }

    const toggleSelect = (idx: number, m: any) => {
        if (m.id === currentMembership?.id || m.id === subscription?.plan?.id) {
            return
        }
        if (selectedIdx === idx) {
            setSelectedIdx(null)
        } else {
            setSelectedIdx(idx)
        }
        console.log('Selected Membership:', m, idx)
        history.push({
            pathname: '/membership/payment',
            state: {
                membership: m,
            },
        })
    }

    return (
        <div className='nb-ms-shell'>
            <div className='nb-ms-header mt-7'>
                <h2 className='nb-ms-title mt-7'>Change your plan</h2>
                <p className='nb-ms-sub'>
                    Please confirm your payment to finalize your Pre-launch Founding Membership
                </p>
            </div>

            <div className='nb-ms-card'>
                <div aria-hidden='true' className='nb-membership-hero'>
                    <img
                        className='nb-membership-hero__img'
                        src={toAbsoluteUrl('/media/login-room.png')}
                        alt=''
                        loading='eager'
                        style={{
                            objectFit: 'cover',
                            display: 'block',
                        }}
                    />
                </div>
                <div className='nb-zoom' ref={zoomRef}>
                    <div className="w-100 tap-flex-center justify-content-start">
                        <div className="nb-link-underline-500 w-auto cursor-pointer" onClick={() => history.goBack()}>
                            Back
                        </div>
                    </div>
                    <div className='nb-board' ref={boardRef}>
                        {/* Columna de labels */}
                        <div className='nb-labels'>
                            <div ref={headRef} className='nb-labels__head'>
                                <span className='nbq-option__title'>BENEFITS</span>
                            </div>

                            {labelRows.map((label, i) => {
                                return (
                                    <div
                                        key={`lbl-${label}`}
                                        ref={(el) => (labelRefs.current[i] = el!)}
                                        className='nb-l-row nb-satoshi-14-300'
                                    >
                                        {getLabelDisplayName(label)}
                                    </div>
                                )
                            })}
                            <div className='nb-labels__btnrow' />
                        </div>

                        {/* Deck de cards */}
                        <div className='nb-deck '>
                            {memberships.map((m: any, idx: number) => {
                                /* hardcore selected memberships */
                                // const hot = hoverIdx === idx || selectedIdx === idx
                                const hot = m.id == membershipUpgrade?.id
                                const rowsCss =
                                    `${headH}px ` +
                                    rowHeights.map((h) => `${Math.max(44, h)}px`).join(' ') +
                                    ` var(--nb-btn-row-h)`

                                const sizeCls = m.featured ? 'nb-plan--lg' : 'nb-plan--sm'

                                return (
                                    <article
                                        key={m.id}
                                        className={`nb-plan ${sizeCls} ${hot ? 'is-hot' : ''}`}
                                        style={{ gridTemplateRows: rowsCss } as React.CSSProperties}
                                        onMouseEnter={() => { setHoverIdx(idx) }}
                                        onMouseLeave={() => { setHoverIdx(null) }}
                                        role='button'
                                        tabIndex={0}
                                        onClick={() => { toggleSelect(idx, m) }}
                                    >
                                        {/* Header */}
                                        <div className='nb-plan__head'>
                                            <div className='nb-oswald-22 mb-3'>{m.title}</div>
                                            <div className='nb-head__divider mb-3' />
                                            <div className='nb-head__price mb-3'>
                                                <div className='nb-oswald-22'>
                                                    {m.priceStr} {m.priceDescription}
                                                </div>
                                            </div>
                                            <div className='nb-head__tag nb-oswald-18'>
                                                {m.priceYear} / {m.interval}
                                            </div>
                                        </div>

                                        {/* Filas */}
                                        {labelRows.map((label) => {
                                            if (label === SECTION_REQ) {
                                                return (
                                                    <div key={`sec-${m.id}`} className='nb-plan__row nb-plan__row--section' />
                                                )
                                            }

                                            if (label === REQUIREMENT_ROW) {
                                                const requirementText = getRequirementText(m)
                                                return (
                                                    <div key={`${m.id}-requirement`} className='nb-plan__row'>
                                                        <span className='nb-requirement-text'>{requirementText}</span>
                                                    </div>
                                                )
                                            }

                                            const booleanValue = getBooleanFieldValue(m, label)
                                            const specialText = getSpecialText(m, label)

                                            if (booleanValue !== null) {
                                                return (
                                                    <div key={`${m.id}-${label}`} className='nb-plan__row'>
                                                        {booleanValue ? (
                                                            <div className='text-center'>
                                                                <div className={`nb-check-mark ${label === 'memberIntroduction' ? 'd-none' : 'd-flex'}`}>✓</div>
                                                                {specialText && (
                                                                    <div className='nb-special-text' style={{ marginTop: '2px' }}>
                                                                        {specialText}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <KTSVG path='/media/svg/nobilis/rem.svg' className='' />
                                                        )}
                                                    </div>
                                                )
                                            }

                                            return (
                                                <div key={`${m.id}-${label}`} className='nb-plan__row'>
                                                    <span className='nb-empty' />
                                                </div>
                                            )
                                        })}

                                        {/* Botón */}
                                        <div className='nb-plan__btnrow'>
                                            {m.id === currentMembership?.id || m.id === subscription?.plan?.id ? (
                                                <button
                                                    type='button'
                                                    className='nb-select-btn'
                                                    disabled
                                                    style={{ opacity: 0.9, cursor: 'default', backgroundColor: 'transparent', borderColor: '#151515', color: '#151515' }}
                                                >
                                                    <span className='nb-heading-md' style={{ color: '#151515', fontWeight: 600 }}>CURRENT PLAN</span>
                                                </button>
                                            ) : (
                                                <button disabled={!hot}
                                                    type='button'
                                                    className={`nb-select-btn ${hot ? 'is-active' : ''}`}
                                                    onClick={(e) => {
                                                        e.stopPropagation()
                                                        toggleSelect(idx, m)
                                                    }}
                                                >
                                                    <span className='txt-inactive'>
                                                        <div className='nb-inactive-button'>
                                                            <div className='nb-inactive-button-text'>INACTIVE</div>
                                                        </div>
                                                    </span>

                                                    <span className='txt-active'>
                                                        <span className='nb-heading-md'>Upgrade PLAN</span>
                                                        <img
                                                            src='/media/svg/nobilis/vector1.svg'
                                                            alt=''
                                                            className='nb-btn-icon nb-btn-icon--black'
                                                        />
                                                    </span>
                                                </button>
                                            )}
                                        </div>
                                    </article>
                                )
                            })}
                        </div>
                    </div>
                </div>
                <div className='nb-disclaimers mt-8'>
                    <p className='nb-disclaimer-text'>
                        *The Founding Member plan will remain active for two years following the official
                        launch. After that, you will transition to either the Electi or Electi+ plan, while
                        retaining the lifelong benefits of being a Founding Member.
                    </p>
                    <p className='nb-disclaimer-text'>
                        ** Required within 30 days of paying the Initiation Fee; non-compliance will lead to
                        restricted profile functionality.
                    </p>
                </div>
            </div>
        </div>
    )
}

export { ChangeMembershipPage }
